/** Court of Master Sommeliers – Advanced/Master deductive tasting grid (DA UI). */

export const SCALE_MM = [
  "Lav",
  "Medium minus",
  "Medium",
  "Medium plus",
  "Høj",
];

export const SCALE_TEARING = [
  "Let",
  "Medium minus",
  "Medium",
  "Medium plus",
  "Kraftig",
];

export const WHITE_PRIMARY = ["Vandhvid", "Strå", "Gul", "Guld"];
export const RED_PRIMARY = ["Lilla", "Rubin", "Granat"];
export const WHITE_SECONDARY = ["Sølv", "Grøn", "Kobber", "Guld", "Brun"];
export const RED_SECONDARY = ["Blå", "Magenta", "Rubin", "Orange", "Granat", "Brun"];

export const WHITE_FRUIT = [
  "Syrelig citrus",
  "Sød citrus",
  "Æble/pære",
  "Stenfrugt",
  "Tropisk",
  "Melon",
  "Andet",
];
export const RED_FRUIT = ["Rød", "Blå", "Sort", "Andet"];

export const FRUIT_CONDITION = [
  "Syrelig",
  "Moden",
  "Frisk",
  "Bagt",
  "Stuvet",
  "Tørret",
  "Bulet",
  "Syltetøjsagtig",
  "Saftig",
  "Kandiseret",
  "Likøragtig",
  "Oxideret",
];

export const NON_FRUIT_GRAPE = [
  "Blomster",
  "Grøn peberfrugt",
  "Asparges",
  "Selleri",
  "Græs",
  "Urter",
  "Tobak",
  "Sort peber",
  "Hvid peber",
  "Ingefær",
  "Honning",
  "Petroleum",
  "Eukalyptus",
  "Chokolade",
  "Mokka",
  "Andet",
];

export const NON_FRUIT_WINEMAKING = [
  "Brøddej",
  "Gær",
  "Øl",
  "Oste-skorpe",
  "Fløde",
  "Crème fraîche",
  "Smør",
  "Yoghurt",
  "Jordnøddeskal",
  "Toast",
  "Marcipan",
  "Stilke",
  "Tyggegummi",
  "Cigarkasse",
  "Andet",
];

export const EARTH_OPTIONS = ["Skovbund", "Svampe/trøfler", "Pottemuld", "Kompost"];
export const MINERAL_OPTIONS = ["Mineral", "Våd sten", "Kalksten", "Kridt", "Skifer", "Flint"];
export const OAK_DESCRIPTORS = [
  "Toast",
  "Røg",
  "Vanilje",
  "Kanel",
  "Nellike",
  "Muskatnød",
  "Kardemomme",
  "Kaffe",
  "Chokolade",
  "Dild",
  "Kokos",
  "Karamel",
  "Ceder",
];

function isRed(answers) {
  const v = answers.sight_colour_type || "";
  return v === "Rød" || v === "Red";
}

function isWhite(answers) {
  const v = answers.sight_colour_type || "";
  return v === "Hvid" || v === "White";
}

function fruitOptions(answers) {
  return isRed(answers) ? RED_FRUIT : WHITE_FRUIT;
}

function isYes(value) {
  return value === "Ja" || value === "Yes";
}

function isNo(value) {
  return value === "Nej" || value === "No";
}

/** Full step catalogue. Use getActiveSteps(answers) for the live path. */
export const WINE_TASTING_STEPS = [
  // —— Sight ——
  {
    id: "sight_clarity",
    section: "Syn",
    label: "Klarhed / synligt bundfald",
    options: ["Klar", "Diset", "Uklar"],
  },
  {
    id: "sight_intensity",
    section: "Syn",
    label: "Farveintensitet",
    options: ["Gennemsigtig", "Bleg", "Medium minus", "Medium", "Medium plus", "Dyb", "Uigennemsigtig"],
  },
  {
    id: "sight_colour_type",
    section: "Syn",
    label: "Vinfarve",
    options: ["Hvid", "Rød"],
  },
  {
    id: "sight_primary_color",
    section: "Syn",
    label: "Primær farve",
    optionsFrom: (answers) => (isRed(answers) ? RED_PRIMARY : WHITE_PRIMARY),
  },
  {
    id: "sight_secondary_color",
    section: "Syn",
    label: "Sekundær farve",
    multi: true,
    allowCustom: true,
    optionsFrom: (answers) => (isRed(answers) ? RED_SECONDARY : WHITE_SECONDARY),
    placeholder: "Tilføj egen farve…",
  },
  {
    id: "sight_rim_variation",
    section: "Syn",
    label: "Farvevariation i kanten",
    hint: "Kun rødvin",
    when: isRed,
    options: ["Ja", "Nej"],
  },
  {
    id: "sight_staining",
    section: "Syn",
    label: "Farveafløb / staining",
    hint: "Kun rødvin",
    when: isRed,
    options: ["Ingen", "Let", "Medium minus", "Medium", "Medium plus", "Kraftig"],
  },
  {
    id: "sight_tearing",
    section: "Syn",
    label: "Tårer / ben",
    options: SCALE_TEARING,
  },
  {
    id: "sight_gas",
    section: "Syn",
    label: "Tegn på gas (CO₂)",
    options: ["Ja", "Nej"],
  },

  // —— Nose ——
  {
    id: "nose_faults",
    section: "Næse",
    label: "Mindre fejl / fault(s)",
    hint: "Notér kun hvis tydeligt — kan springes over",
    skippable: true,
    multi: true,
    chips: true,
    allowCustom: true,
    options: [
      "Kostald",
      "Neglelakfjerner",
      "Karamel",
      "Nødder",
      "Tørret frugt",
      "Strygejern / tændstik",
      "Balsamico",
      "Andet",
    ],
    placeholder: "Anden fejl…",
  },
  {
    id: "nose_intensity",
    section: "Næse",
    label: "Aromatisk intensitet",
    options: ["Delikat", "Medium minus", "Medium", "Medium plus", "Kraftig"],
  },
  {
    id: "nose_age",
    section: "Næse",
    label: "Aldersvurdering",
    options: ["Ung", "Under udvikling (viser alder)", "Moden / aged"],
  },
  {
    id: "nose_fruit",
    section: "Næse",
    label: "Frugt",
    multi: true,
    chips: true,
    allowCustom: true,
    optionsFrom: fruitOptions,
    placeholder: "Tilføj frugtnote…",
  },
  {
    id: "nose_fruit_condition",
    section: "Næse",
    label: "Frugttilstand",
    multi: true,
    chips: true,
    allowCustom: true,
    options: FRUIT_CONDITION,
    placeholder: "Tilføj frugttilstand…",
  },
  {
    id: "nose_non_fruit",
    section: "Næse",
    label: "Ikke-frugt",
    hint: "Fra drue(r) og vinifikation",
    multi: true,
    chips: true,
    allowCustom: true,
    options: [...NON_FRUIT_GRAPE, ...NON_FRUIT_WINEMAKING],
    placeholder: "Tilføj note…",
  },
  {
    id: "nose_earth",
    section: "Næse",
    label: "Jord / earth",
    skippable: true,
    multi: true,
    chips: true,
    allowCustom: true,
    options: EARTH_OPTIONS,
    placeholder: "Tilføj jord-note…",
  },
  {
    id: "nose_mineral",
    section: "Næse",
    label: "Mineralitet",
    skippable: true,
    multi: true,
    chips: true,
    allowCustom: true,
    options: MINERAL_OPTIONS,
    placeholder: "Tilføj mineral-note…",
  },
  {
    id: "nose_oak_present",
    section: "Næse",
    label: "Er der ny eg?",
    options: ["Ja", "Nej"],
  },
  {
    id: "nose_oak_intensity",
    section: "Næse",
    label: "Eg-intensitet",
    when: (a) => isYes(a.nose_oak_present),
    options: ["Lav", "Medium", "Høj"],
  },
  {
    id: "nose_oak_descriptors",
    section: "Næse",
    label: "Eg-deskriptorer",
    when: (a) => isYes(a.nose_oak_present),
    multi: true,
    chips: true,
    allowCustom: true,
    options: OAK_DESCRIPTORS,
    placeholder: "Tilføj eg-note…",
  },
  {
    id: "nose_oak_type",
    section: "Næse",
    label: "Eg-type",
    when: (a) => isYes(a.nose_oak_present),
    options: ["Fransk", "Amerikansk", "Østeuropæisk"],
  },

  // —— Palate structure ——
  {
    id: "structure_sweetness",
    section: "Struktur",
    label: "Sødme",
    options: [
      "Knastør",
      "Tør",
      "Tør med let restsødme",
      "Halvtør",
      "Medium-sød",
      "Sød",
      "Meget sød",
    ],
  },
  {
    id: "structure_acidity",
    section: "Struktur",
    label: "Syre",
    options: SCALE_MM,
  },
  {
    id: "structure_alcohol",
    section: "Struktur",
    label: "Alkohol",
    options: SCALE_MM,
  },
  {
    id: "structure_body",
    section: "Struktur",
    label: "Krop / fylde",
    options: ["Let", "Medium minus", "Medium", "Medium plus", "Fuld"],
  },
  {
    id: "structure_phenolic",
    section: "Struktur",
    label: "Fenolisk bitterhed",
    hint: "Kun hvidvin",
    when: isWhite,
    options: ["Ja", "Nej"],
  },
  {
    id: "structure_tannin_volume",
    section: "Struktur",
    label: "Tannin-mængde",
    hint: "Kun rødvin",
    when: isRed,
    options: SCALE_MM,
  },
  {
    id: "structure_tannin_texture",
    section: "Struktur",
    label: "Tannin-tekstur",
    hint: "Kun rødvin",
    when: isRed,
    options: ["Silkeblød", "Blød", "Grynig", "Groft", "Ru", "Stilket", "Hård", "Andet"],
  },
  {
    id: "structure_texture",
    section: "Struktur",
    label: "Tekstur",
    hint: "Notér kun hvis tydeligt",
    skippable: true,
    multi: true,
    allowCustom: true,
    options: [
      "Slank",
      "Lineær",
      "Sprød",
      "Blød",
      "Cremé",
      "Rund",
      "Voksagtig",
      "Olieagtig",
      "Viskøs",
      "Andet",
    ],
    placeholder: "Anden tekstur…",
  },

  // —— Palate ——
  {
    id: "palate_balance",
    section: "Gane",
    label: "Balance",
    options: ["Ja", "Nej"],
  },
  {
    id: "palate_balance_notes",
    section: "Gane",
    label: "Hvad dominerer vinen?",
    when: (a) => isNo(a.palate_balance),
    freeText: true,
    placeholder: "Hvilke elementer dominerer?",
  },
  {
    id: "palate_length",
    section: "Gane",
    label: "Eftersmagens længde",
    options: ["Kort", "Medium minus", "Medium", "Medium plus", "Lang"],
  },
  {
    id: "palate_complexity",
    section: "Gane",
    label: "Kompleksitet",
    options: ["Enkel", "Medium minus", "Medium", "Medium plus", "Høj"],
  },

  // —— Initial conclusion ——
  {
    id: "initial_grape",
    section: "Foreløbig konklusion",
    label: "Mulige druesorter",
    freeText: true,
    placeholder: "Mulige druer / blends…",
    blindOnly: true,
  },
  {
    id: "initial_climate",
    section: "Foreløbig konklusion",
    label: "Klima",
    options: ["Køligt", "Moderat", "Varmt"],
    blindOnly: true,
  },
  {
    id: "initial_countries",
    section: "Foreløbig konklusion",
    label: "Mulige lande",
    freeText: true,
    placeholder: "Mulige lande…",
    blindOnly: true,
  },
  {
    id: "initial_regions",
    section: "Foreløbig konklusion",
    label: "Mulige regioner",
    freeText: true,
    placeholder: "Fx Rhône, Bordeaux, Bourgogne, Piemonte, Rioja…",
    blindOnly: true,
  },
  {
    id: "initial_age",
    section: "Foreløbig konklusion",
    label: "Aldersinterval",
    options: ["1–3 år", "3–5 år", "5–10 år", "10+ år"],
    blindOnly: true,
  },

  // —— Final conclusion ——
  {
    id: "final_grape",
    section: "Endelig konklusion",
    label: "Druesort eller blend",
    freeText: true,
    placeholder: "Fx Nebbiolo, Chardonnay, GSM-blend…",
    blindOnly: true,
  },
  {
    id: "final_country",
    section: "Endelig konklusion",
    label: "Oprindelsesland",
    freeText: true,
    placeholder: "Fx Italien, Frankrig, USA…",
    blindOnly: true,
  },
  {
    id: "final_appellation",
    section: "Endelig konklusion",
    label: "Appellation / GI / AVA",
    hint: "Region + beskyttet navn, hvor det er relevant",
    freeText: true,
    placeholder: "Fx Barolo DOCG, Gevrey-Chambertin, Rutherford AVA, Mosel…",
    blindOnly: true,
  },
  {
    id: "final_vintage",
    section: "Endelig konklusion",
    label: "Årgang",
    hint: "Vælg én",
    freeText: true,
    placeholder: "Fx 2019",
    blindOnly: true,
  },
  {
    id: "final_producer",
    section: "Endelig konklusion",
    label: "Producent / domaine",
    hint: "Hvis du kan gætte eller udlede producenten",
    freeText: true,
    skippable: true,
    placeholder: "Fx Gaja, Domaine Leflaive, Ridge…",
    blindOnly: true,
  },
  {
    id: "final_quality_level",
    section: "Endelig konklusion",
    label: "Officielt kvalitetsniveau",
    hint: "Hvor det er relevant",
    skippable: true,
    blindOnly: true,
    options: [
      "Village",
      "Premier Cru",
      "Grand Cru",
      "Reserva",
      "Gran Reserva",
      "Normale",
      "Riserva",
      "Classico",
      "Gran Selezione",
      "Kabinett",
      "Spätlese",
      "Auslese",
      "Andet",
    ],
  },
  {
    id: "final_style",
    section: "Endelig konklusion",
    label: "Officiel stil-kategori",
    hint: "Hvor det er relevant",
    skippable: true,
    blindOnly: true,
    options: ["Trocken", "Sec", "Demi-Sec", "Moelleux", "Aszú", "VT", "SGN", "Andet"],
  },
  {
    id: "tasting_notes",
    section: "Noter",
    label: "Yderligere noter",
    freeText: true,
    multiline: true,
    skippable: true,
    placeholder: "Frie noter til smagningen…",
    sightedOnly: true,
  },
];

export const FINAL_SECTION = "Endelig konklusion";

export const SECTION_HEADINGS = {
  Syn: "SYN",
  Næse: "NÆSE",
  Struktur: "STRUKTUR",
  Gane: "GANE",
  "Foreløbig konklusion": "FORELØBIG KONKLUSION",
  "Endelig konklusion": "ENDELIG KONKLUSION",
  Noter: "NOTER",
};

export const GRID_LEFT_SECTIONS = ["Syn", "Næse"];
export const GRID_RIGHT_SECTIONS = [
  "Struktur",
  "Gane",
  "Foreløbig konklusion",
  "Endelig konklusion",
  "Noter",
];

export function stepOptions(step, answers) {
  if (!step) return [];
  if (typeof step.optionsFrom === "function") return step.optionsFrom(answers) || [];
  return step.options || [];
}

function stepAllowedForMode(step, blind) {
  if (blind) {
    if (step.sightedOnly) return false;
    return true;
  }
  if (step.blindOnly) return false;
  return true;
}

export function getActiveSteps(answers = {}, { blind = true } = {}) {
  return WINE_TASTING_STEPS.filter((step) => {
    if (!stepAllowedForMode(step, blind)) return false;
    return !step.when || step.when(answers);
  });
}

export function getTastingSteps(answers = {}, { blind = true } = {}) {
  return getActiveSteps(answers, { blind }).filter((step) => step.section !== FINAL_SECTION);
}

export function getFinalSteps(answers = {}, { blind = true } = {}) {
  if (!blind) return [];
  return getActiveSteps(answers, { blind }).filter((step) => step.section === FINAL_SECTION);
}

export function groupStepsBySection(steps) {
  const groups = [];
  let current = null;
  for (const step of steps) {
    if (!current || current.section !== step.section) {
      current = { section: step.section, steps: [] };
      groups.push(current);
    }
    current.steps.push(step);
  }
  return groups;
}

export function parseMultiAnswer(value) {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  const raw = String(value || "").trim();
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
  } catch {
    /* not json */
  }
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function formatMultiAnswer(values) {
  const cleaned = [...new Set((values || []).map((v) => String(v).trim()).filter(Boolean))];
  return cleaned.length ? JSON.stringify(cleaned) : "";
}

export function formatAnswerForDisplay(value) {
  const multi = parseMultiAnswer(value);
  if (multi.length) return multi.join(", ");
  return String(value || "").trim();
}

export function parseWineTastingPayload(body) {
  const raw = String(body || "");
  const match = raw.match(/<!--\s*wine-tasting:v\d+\s+(\{[\s\S]*?\})\s*-->/);
  if (!match) return null;
  try {
    return JSON.parse(match[1]);
  } catch {
    return null;
  }
}

export function emptyWineIdentity() {
  return {
    name: "",
    producer: "",
    grape: "",
    region: "",
    vintage: "",
    grapeminds_id: "",
  };
}

export function wineFromGrapeMinds(detail) {
  if (!detail) return emptyWineIdentity();
  return {
    name: detail.name || "",
    producer: detail.producer || "",
    grape: detail.grape || "",
    grapes: Array.isArray(detail.grapes) ? detail.grapes : [],
    region: detail.region || "",
    region_name: detail.region_name || "",
    country: detail.country || "",
    vintage: detail.vintage || "",
    grapeminds_id: detail.id != null ? String(detail.id) : "",
    color: detail.color || "",
    residual_sugar: detail.residual_sugar || "",
    flavor_profile: detail.flavor_profile || null,
  };
}

export function buildWineTastingPayload({
  wine,
  answers,
  comparisons = null,
  expected = null,
  blind = true,
}) {
  return {
    version: 3,
    grid: "cms-advanced",
    blind: Boolean(blind),
    wine: {
      name: (wine.name || "").trim(),
      producer: (wine.producer || "").trim(),
      grape: (wine.grape || "").trim(),
      grapes: Array.isArray(wine.grapes) ? wine.grapes : [],
      region: (wine.region || "").trim(),
      region_name: (wine.region_name || "").trim(),
      country: (wine.country || "").trim(),
      vintage: (wine.vintage || "").trim(),
      grapeminds_id: (wine.grapeminds_id || wine.slug || "").trim(),
      color: wine.color || "",
      residual_sugar: wine.residual_sugar || "",
      flavor_profile: wine.flavor_profile || null,
    },
    answers: { ...(answers || {}) },
    expected: expected || null,
    comparisons: comparisons
      ? Object.fromEntries(Object.entries(comparisons).filter(([k]) => k !== "__expected"))
      : null,
  };
}

export function buildWineTastingBody({
  wine,
  answers,
  comparisons = null,
  expected = null,
  blind = true,
}) {
  const payload = buildWineTastingPayload({ wine, answers, comparisons, expected, blind });

  const lines = [
    `<!-- wine-tasting:v3 ${JSON.stringify(payload)} -->`,
    "",
    payload.blind ? "## Vin (afsløret efter smagning)" : "## Vin",
    "",
    `- **Navn:** ${payload.wine.name || "—"}`,
    `- **Producent:** ${payload.wine.producer || "—"}`,
    `- **Drue:** ${payload.wine.grape || "—"}`,
    `- **Region:** ${payload.wine.region || "—"}`,
    `- **Årgang:** ${payload.wine.vintage || "—"}`,
  ];
  if (payload.wine.grapeminds_id) {
    lines.push(`- **GrapeMinds ID:** ${payload.wine.grapeminds_id}`);
  }
  lines.push("");

  let currentSection = "";
  for (const step of getActiveSteps(answers, { blind: payload.blind })) {
    if (step.section !== currentSection) {
      currentSection = step.section;
      lines.push(`## ${currentSection}`, "");
    }
    const value = formatAnswerForDisplay(answers[step.id]) || "—";
    lines.push(`- **${step.label}:** ${value}`);
  }
  lines.push("");
  return lines.join("\n");
}

export function defaultWineTastingTitle(wine, { blind = true } = {}) {
  const name = (wine?.name || "").trim();
  const date = new Date().toLocaleDateString("da-DK", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  const prefix = blind ? "Blind smagning" : "Smagning";
  if (name) return `${prefix} · ${name}`;
  return `${prefix} · ${date}`;
}
